import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Bell, X, AlertTriangle, TrendingDown, Flame, GitPullRequest, CheckCircle } from "lucide-react";

import { API } from "../lib/api";

const ALERT_ICONS = {
  pillar_critical: AlertTriangle,
  score_drop: TrendingDown,
  burnout_risk: Flame,
  misalignment: GitPullRequest,
};

const SEVERITY_STYLES = {
  critical: "border-l-red-500 bg-red-50",
  high: "border-l-orange-500 bg-orange-50",
  medium: "border-l-amber-500 bg-amber-50",
};

export default function AlertBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/alerts/unread-count`, { withCredentials: true });
      setCount(data.count);
    } catch {}
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/alerts`, { withCredentials: true });
      setAlerts(data);
    } catch {}
    finally { setLoading(false); }
  };

  const handleOpen = () => {
    setOpen(!open);
    if (!open) fetchAlerts();
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/api/alerts/${id}/read`, {}, { withCredentials: true });
      setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, read: true } : a));
      setCount((prev) => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/api/alerts/read-all`, {}, { withCredentials: true });
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
      setCount(0);
    } catch {}
  };

  return (
    <div className="relative">
      <button data-testid="alert-bell" onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors">
        <Bell className="w-4 h-4 text-slate-500" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-outfit font-bold rounded-full flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden animate-slide-up">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="font-outfit text-sm font-bold text-slate-900">Alerts</h3>
              <div className="flex items-center gap-2">
                {count > 0 && (
                  <button onClick={markAllRead} className="font-ibm-plex text-xs text-blue-600 hover:text-blue-800">
                    Mark all read
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center">
                  <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : alerts.length === 0 ? (
                <div className="p-6 text-center">
                  <CheckCircle className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
                  <p className="font-ibm-plex text-sm text-slate-400">No alerts — system is healthy</p>
                </div>
              ) : (
                alerts.slice(0, 15).map((alert) => {
                  const Icon = ALERT_ICONS[alert.type] || AlertTriangle;
                  const sev = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.medium;
                  return (
                    <div key={alert.id} data-testid={`alert-${alert.id}`}
                      onClick={() => !alert.read && markRead(alert.id)}
                      className={`px-4 py-3 border-b border-gray-50 border-l-4 cursor-pointer transition-all ${
                        alert.read ? "border-l-transparent bg-white opacity-60" : sev
                      } hover:bg-gray-50`}>
                      <div className="flex items-start gap-2.5">
                        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${alert.read ? "text-slate-400" : "text-red-500"}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`font-outfit text-sm font-semibold ${alert.read ? "text-slate-400" : "text-slate-900"}`}>
                            {alert.title}
                          </p>
                          <p className="font-ibm-plex text-xs text-slate-500 mt-0.5 line-clamp-2">{alert.message}</p>
                          <p className="font-ibm-plex text-[10px] text-slate-300 mt-1">
                            {alert.project_name} · {new Date(alert.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
